import t from "@repo/localization/string.macro";
import { ReportAccount as CanadaReportAccountPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/credit/report_account_pb";
import {
  AccountPreview as UsAccountPreviewPb,
  AccountV2 as UsAccountV2Pb,
} from "@repo/protobuf/gen/web/public/credit_pb";
import Protobuf from "@repo/protobuf/utils";
import Telemetry from "@repo/telemetry";
import Address from "@repo/utils/Address";
import Case from "@repo/utils/Case";
import Enum from "@repo/utils/Enum";
import UArray from "@repo/utils/UArray";

import { AddressUsa } from "../Address";

type ReportAccount = {
  id: ReportAccount.Id;
  name: string;
  type: ReportAccount.Type;
  openedAt: Date;
  closedAt?: Date;
  isOpen: boolean;
  lastReportedAt: Date;
  currentBalanceCents: number;
  creditLimitAmountCents: number;
  pastDueAmountCents: number;
  paymentHistory: ReportAccount.Payment[];
  contactInfo?: ReportAccount.ContactInfo;
  message: string;
};

namespace ReportAccount {
  export type Id = string & {};

  export const types = [
    "other",
    "installment",
    "revolving",
    "mortgage",
    "inquiry",
    "collection",
  ] as const;
  export type Type = (typeof types)[number];

  export namespace Type {
    const iconByType: Record<Type, string> = {
      revolving: "💳",
      installment: "💰",
      mortgage: "🏡",
      inquiry: "",
      collection: "",
      other: "💵",
    };
    export const icon = (type: Type) => iconByType[type];
    export const format = (() => {
      const map = {
        installment: () => t`Installment`,
        revolving: () => t`Revolving`,
        mortgage: () => t`Mortgage`,
        inquiry: () => t`Inquiry`,
        collection: () => t`Collection`,
        other: () => t`Other`,
      } satisfies Record<Type, () => string>;
      return (type: Type) => map[type]();
    })();
  }
  export namespace Payment {
    export type Status =
      | "other"
      | "current"
      | "unavailable"
      | "lateLessThan30Days"
      | "late30Days"
      | "late60Days"
      | "late90Days"
      | "late120Days"
      | "chargeOff"
      | "collection"
      | "repossession"
      | "zeroBalAndCurrAcct"
      | "notReported"
      | "collectionChargeoff";
  }

  export type Payment = {
    date: Date;
    status: Payment.Status;
  };

  export type ContactInfo = {
    name: string;
    address?: Address;
    phone: string;
  };

  export namespace Preview {
    export type MeasureType =
      | "utilization"
      | "balance"
      | "onTimePayments"
      | "missedPayments"
      | "accountCode"
      | "creditAge"
      | "collections"
      | "accountStatus";
  }

  export type Preview = {
    id: ReportAccount.Id;
    type: ReportAccount.Type;
    name: string;
    measureType: Preview.MeasureType;
    measureValue: string;
    isOpen: boolean;
    isKikoffAccount: boolean;
  };
}

export default ReportAccount;

export namespace ReportAccountUsa {
  export function normalize(proto: UsAccountV2Pb): ReportAccount {
    return {
      id: proto.id,
      contactInfo: proto.contactInfo && {
        address: AddressUsa.normalize(proto.contactInfo.address),
        name: proto.contactInfo.name,
        phone: proto.contactInfo.phoneNumber,
      },
      creditLimitAmountCents: proto.creditLimitAmountDollars * 100,
      currentBalanceCents: proto.balanceAmountDollars * 100,
      pastDueAmountCents: proto.pastDueAmountDollars * 100,
      paymentHistory: proto.paymentHistory.map(Payment.normalize),
      name: proto.name,
      // TODO: Figure out what exactly description is
      message: proto.message,
      lastReportedAt: Protobuf.Timestamp.toDate(proto.reportedDate!),
      openedAt: Protobuf.Timestamp.toDate(proto.dateOpened!),
      type: (() => {
        const type = Case.fromConstant(proto.accountType).toCamel();

        if (!UArray.includesLiteral(ReportAccount.types, type)) {
          Telemetry.captureError(new Error(`Unexpected account type: ${type}`));

          return "other";
        }

        return type;
      })(),
      closedAt: Protobuf.Timestamp.toDate(proto.closedAt),
      // Fixture includes many accounts that match !account.accountOpen and
      // !account.closedAt. We will rely on `accountOpen` to stay consistent
      // with react codebase
      isOpen: proto.accountOpen,
    };
  }

  export namespace Payment {
    const paymentStatusMap: Record<
      Exclude<
        Case.ToCamel<
          Case.FromConstant<keyof typeof UsAccountV2Pb.PaymentInfo.Value>
        >,
        ReportAccount.Payment.Status
      >,
      ReportAccount.Payment.Status
    > = {
      dayslate30: "late30Days",
      dayslate60: "late60Days",
      dayslate90: "late90Days",
      dayslate120: "late120Days",
      over120DaysPastDue: "late120Days",
      paysAsAgreed: "current",
    };

    export function normalize(
      proto: UsAccountV2Pb.PaymentInfo
    ): ReportAccount.Payment {
      const status = Case.fromConstant(
        Enum.keyOf(UsAccountV2Pb.PaymentInfo.Value, proto.value)
      ).toCamel();

      return {
        date: Protobuf.Timestamp.toDate(proto.paymentTime!),
        status: (paymentStatusMap as any)[status] || status,
      };
    }
  }

  export namespace Preview {
    export function normalize(
      proto: UsAccountPreviewPb
    ): ReportAccount.Preview {
      const type = Case.fromConstant(
        Enum.keyOf(UsAccountPreviewPb.AccountType, proto.accountType)
      ).toCamel();

      const measureType = Case.fromConstant(
        Enum.keyOf(UsAccountPreviewPb.MeasureType, proto.measureType)
      ).toCamel();

      if (measureType === "unknown")
        Telemetry.captureError(new Error("Unexpected UNKNOWN measureType."));

      return {
        id: proto.id,
        type,
        isKikoffAccount: proto.isKikoffAccount,
        isOpen: proto.isOpen,
        measureType:
          measureType === "unknown" ? ("unknown" as never) : measureType,
        measureValue: proto.measureValue,
        name: proto.name,
      };
    }
  }
}

export namespace ReportAccountCanada {
  export function normalize(proto: CanadaReportAccountPb): ReportAccount {
    const type = Case.fromConstant(
      Enum.keyOf(CanadaReportAccountPb.Type, proto.type)
    ).toCamel();
    return {
      ...proto,
      type,
      openedAt: Protobuf.Timestamp.toDate(proto.openedAt!),
      closedAt: Protobuf.Timestamp.toDate(proto.closedAt),
      lastReportedAt: Protobuf.Timestamp.toDate(proto.lastReportedAt!),
      paymentHistory: proto.paymentHistory.map(Payment.normalize),
      message: "",
      isOpen: proto.closedAt == null,
    };
  }
  export namespace Payment {
    export function normalize(
      proto: CanadaReportAccountPb.Payment
    ): ReportAccount.Payment {
      const status = Case.fromConstant(
        Enum.keyOf(CanadaReportAccountPb.Payment.Status, proto.status)
      ).toCamel();
      return {
        date: Protobuf.Timestamp.toDate(proto.date!),
        status,
      };
    }
  }
  export namespace Preview {
    export function normalize(
      proto: CanadaReportAccountPb.Preview
    ): ReportAccount.Preview {
      const measureType = Case.fromConstant(
        Enum.keyOf(CanadaReportAccountPb.Preview.MeasureType, proto.measureType)
      ).toCamel();

      if (measureType === "unknown")
        throw new Error("Unexpected UNKNOWN measureType.");

      const accountType = Case.fromConstant(
        Enum.keyOf(CanadaReportAccountPb.Type, proto.accountType)
      ).toCamel();

      return {
        ...proto,
        measureType,
        type: accountType,
      };
    }
  }
}
